// ** Course player top bar (style-v3 aligned with the main app header)

import React from "react";
import { useLocation } from "react-router-dom";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";

// ** MUI Imports
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

// ** Icons
import ChevronLeft from "mdi-material-ui/ChevronLeft";

// ** Components
import ModeToggler from "src/@core/layouts/components/shared-components/ModeToggler";
import UserDropdown from "src/layouts/components/UserDropdown";
import LessonSearch from "src/@core/layouts/components/shared-components/LessonSearch";

const isMobile = window.innerWidth <= 760;

// Rounded header control, shared with LessonSearch so back / search / theme
// controls all read the same (matches the main app header).
const controlSx = {
  width: 40,
  height: 40,
  borderRadius: "12px",
  color: "text.secondary",
  transition: "color .2s, background-color .2s",
  "&:hover": { color: "primary.main", backgroundColor: (theme) => `${theme.palette.primary.main}14` },
};

/******************************************************************
 * ##: AppBarHeader
 * Top bar of the course player: back-to-courses control, optional
 * producer brand logo, course title, lesson search, theme toggle and
 * account menu. Restyled to match the style-v3 app header; colours
 * come from the MUI theme so it follows light / dark.
 * @param {Object} props - title / settings / logos / lessons / handlers
 * @returns {JSX.Element} - the player top bar
 * History:
 * 05-07-2026: Restyled to match the v3 header (surface, rounded controls)
 ******************************************************************/
function AppBarHeader({ title, settings, saveSettings, whiteLogo, darkLogo, lessons, activeLessonHandler }) {
  const location = useLocation();
  const homeHref = pathWithPreservedSearch("/home", location.search);
  const brandLogo = settings.mode === "light" ? whiteLogo : darkLogo;

  return (
    <Box
      className="video-layout-top-bar"
      sx={{
        px: { xs: 2, sm: 4 },
        gap: 1,
        backgroundColor: "background.paper",
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 12px rgba(44, 46, 68, 0.06)",
      }}
    >
      <Box className="actions-left" sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0, mr: 2 }}>
        <Tooltip title="Voltar aos cursos">
          <IconButton href={homeHref} aria-label="Voltar aos cursos" sx={controlSx}>
            <ChevronLeft />
          </IconButton>
        </Tooltip>

        {whiteLogo && darkLogo && <img className="app-bar-logo" src={brandLogo} alt="logo" />}

        {(!isMobile || !whiteLogo) && (
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: 14, sm: 17 },
              color: "text.primary",
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "1",
              WebkitBoxOrient: "vertical",
            }}
          >
            {title}
          </Typography>
        )}
      </Box>

      <Box className="actions-right" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LessonSearch lessons={lessons} activeLessonHandler={activeLessonHandler} isMobile={isMobile} controlSx={controlSx} />
        <ModeToggler settings={settings} saveSettings={saveSettings} />
        <UserDropdown settings={settings} />
      </Box>
    </Box>
  );
}

export default AppBarHeader;
